import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { trackEvent } from './analytics';
import Home from './Home';
import HowItWorks from './HowItWorks';
import JoinUs from './JoinUs';
import Safety from './Safety';
import PrivacyPolicy from './PrivacyPolicy';
import Download from './Download';
import PartnerApply from './PartnerApply';
import PartnerLanding from './PartnerLanding';
import TermsOfService from './TermsOfService';
import RefundPolicy from './RefundPolicy';
import AccountDeletion from './AccountDeletion';
import NotFound from './NotFound';
import LandingPage from './landing/LandingPage';

// Only the multi-step partner onboarding flow is code-split: it carries real
// weight (camera/face-detection, audio & video recorders, canvas processing)
// that marketing pages shouldn't have to download. Those marketing pages stay
// in the main bundle above — lazy-loading a page as frequently-landed-on as
// Home would add a chunk-fetch round trip to its own first paint, which is a
// net loss. This split was verified with Lighthouse before/after, not guessed.
const PartnerOTP = lazy(() => import('./PartnerOTP'));
const PartnerPersonal = lazy(() => import('./PartnerPersonal'));
const PartnerProfileSetup = lazy(() => import('./PartnerProfileSetup'));
const PartnerProfileDetails = lazy(() => import('./PartnerProfileDetails'));
const PartnerEarn = lazy(() => import('./PartnerEarn'));
const AudioCongrats = lazy(() => import('./AudioCongrats'));
const PartnerVideoIntro = lazy(() => import('./PartnerVideoIntro'));
const PartnerVideoUpload = lazy(() => import('./PartnerVideoUpload'));
const PartnerCameraVerification = lazy(() => import('./PartnerCameraVerification'));
const PartnerFacialSuccess = lazy(() => import('./PartnerFacialSuccess'));
const PartnerProfilePic = lazy(() => import('./PartnerProfilePic'));
const PartnerKYC = lazy(() => import('./PartnerKYC'));
const PartnerRules = lazy(() => import('./PartnerRules'));
const PartnerSuccess = lazy(() => import('./PartnerSuccess'));

// Blog: also code-split, same rationale as the partner flow above but for a
// different reason — it pulls in react-markdown + the remark/rehype plugin
// chain, weight no other page on the site needs. One chunk covers the whole
// /blog/* subtree, so navigating between blog pages after the first load
// costs nothing further.
const BlogHome = lazy(() => import('./blog/BlogHome'));
const BlogPost = lazy(() => import('./blog/BlogPost'));
const CategoryArchive = lazy(() => import('./blog/CategoryArchive'));
const TagArchive = lazy(() => import('./blog/TagArchive'));
const AuthorArchive = lazy(() => import('./blog/AuthorArchive'));

// GA4/Firebase Analytics only auto-logs a page_view on the very first load of
// an SPA — client-side route changes (every internal <Link> click) are
// invisible to it otherwise. Without this, the "Pages and screens" report
// would only ever show whatever URL each visitor first landed on, never
// where they actually navigated to inside the app.
function RouteTracker() {
  const location = useLocation();
  const isFirstRender = useRef(true);
  useEffect(() => {
    trackEvent('page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
    // The Meta Pixel base script in public/index.html already fires PageView
    // for the very first load — only re-fire it on later client-side
    // navigations, or the ad's landing views won't be tracked at all since
    // an SPA route change never triggers a real page load.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <RouteTracker />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/join-us" element={<JoinUs />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/download" element={<Download />} />
        <Route path="/partner-landing" element={<PartnerLanding />} />
        <Route path="/partner-apply" element={<PartnerApply />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/account-deletion" element={<AccountDeletion />} />

        {Object.entries({
          '/partner-otp': PartnerOTP,
          '/partner-personal': PartnerPersonal,
          '/partner-profile-setup': PartnerProfileSetup,
          '/partner-profile-details': PartnerProfileDetails,
          '/partner-earn': PartnerEarn,
          '/audio-congrats': AudioCongrats,
          '/partner-video-intro': PartnerVideoIntro,
          '/video-upload': PartnerVideoUpload,
          '/camera-verification': PartnerCameraVerification,
          '/facial-success': PartnerFacialSuccess,
          '/profile-pic': PartnerProfilePic,
          '/kyc-upload': PartnerKYC,
          '/rules': PartnerRules,
          '/partner-success': PartnerSuccess,
        }).map(([path, Component]) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={null}>
                <Component />
              </Suspense>
            }
          />
        ))}

        {/* Blog (see src/blog/ and content/blog/README.md). Registered
            above the /:slug landing-page route so /blog itself can't be
            shadowed; the nested paths below it (/blog/my-post, etc.) are
            two-plus segments and were never reachable via /:slug anyway. */}
        {[
          { path: '/blog', Component: BlogHome },
          { path: '/blog/:slug', Component: BlogPost },
          { path: '/blog/category/:slug', Component: CategoryArchive },
          { path: '/blog/tag/:slug', Component: TagArchive },
          { path: '/blog/author/:slug', Component: AuthorArchive },
        ].map(({ path, Component }) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={null}>
                <Component />
              </Suspense>
            }
          />
        ))}

        {/* SEO landing pages (see src/landing/landingData.js). One dynamic
            route serves all of them from a shared template — shipping a new
            one is "add an object to landingData", not "add a route here".
            React Router ranks static paths above a param segment regardless
            of declaration order, so this can't shadow any route above it. */}
        <Route path="/:slug" element={<LandingPage />} />

        {/* Unmatched paths: render a real 404 UI with noindex rather than
            silently falling through to a blank/duplicate shell. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
